import API from '@/amplify/api';
import Storage from '@/amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { createThumbnail } from '@/graphql/mutations';

export const createThumbnailAPI = async req => {
    let paramsWithConfigOptions = {
        query: createThumbnail,
        variables: {
            createThumbnailRequestDto: {
                ...req
            }
        }
    };

    try {
        const res = await API.graphql(paramsWithConfigOptions);
        const createdThumbnail = res?.data?.createThumbnail;
        return createdThumbnail;
    } catch (err) {
        console.log('createThumbnailAPI err: ', err);
        const { errors } = err;
        if (!Utils.isEmptyArray(errors)) {
            const errorType = errors[0]?.errorType;
            if (errorType === "Lambda:ExecutionTimeoutException") {
                // handle fake body
                return {
                    statusCode: 200,
                    body: JSON.stringify(req.fileList.map(fileStr => {
                        const file = Utils.convertJSONDataToObject(fileStr);
                        const { small, medium, large } = Utils.getThumbnailPaths(file?.s3Path);
                        return {
                            file,
                            thumbnailInfo: {
                                small: { thumbnailUri: small },
                                medium: { thumbnailUri: medium },
                                large: { thumbnailUri: large },
                            }
                        };
                    }))
                };
            }
        }
        return null;
    }
};

const createThumbnailOnAiChat = async (fileList, actionType) => {
    if (Utils.isEmptyArray(fileList)) return;
    const queryParams = {
        actionType,
        fileList: fileList.map(file => JSON.stringify(file)),
        attachBy: Constant.ATTACH_FILE.ATTACH_BY.AI_CHAT
    };
    const response = await createThumbnailAPI(queryParams);
    if (!response || Number(response.statusCode) !== 200) {
        return [];
    }

    return Utils.convertJSONDataToObject(response.body);
};

/**
 * Upload images to S3 for AI Chat and create their thumbnails.
 * Returns one entry per input image, including failed uploads (file.s3Path = null),
 * so the caller can mark them as ERROR. A thumbnail failure does not drop a successful upload.
 * @param {string} path - S3 path prefix
 * @param {Object[]} listImage - List of image items with id and fileData
 * @returns {Object[]} - List of { file: { id, s3Path }, thumbnailInfo }
 */
export const uploadImagesTmpOnAiChat = async (path, listImage) => {
    if (Utils.isEmptyArray(listImage)) return [];

    const putImageS3 = await Promise.all(
        listImage.map(async img => {
            const uploaded = await uploadImageToS3(path, img.fileData, true);
            return {
                id: img?.id,
                s3Path: uploaded,
            };
        })
    );

    // Only uploaded images need a thumbnail
    let thumbnailData = [];
    try {
        const uploadedImages = putImageS3.filter(data => Boolean(data.s3Path));
        thumbnailData = (await createThumbnailOnAiChat(uploadedImages, Constant.CREATE_THUMBNAIL_ACTION_TYPE.CREATE_THUMBNAIL_FOR_TMP_IMG)) || [];
    } catch (error) {
        console.log('uploadImagesTmpOnAiChat create thumbnail error: ', error);
    }

    return putImageS3.map(data => {
        const thumbnail = thumbnailData.find(item => item?.file?.id === data.id);
        return thumbnail || { file: data, thumbnailInfo: null };
    });
};

/**
 * Upload a file to S3.
 * @param {string} path - S3 path prefix
 * @param {File} file - File to upload
 * @param {boolean} [useChecksum] - When true, skips the post-multipart HeadObject integrity
 * check (IAM role for this path only has s3:PutObject/DeleteObject, not s3:GetObject, so
 * HeadObject would 403 on files >5MB/multipart even though the upload itself succeeded).
 * Opt-in only, so other callers of this shared function keep the previous behavior.
 * @returns {string|null} - S3 path on success, null on failure
 */
export const uploadImageToS3 = async (path, file, useChecksum = false) => {
    try {
        if (!file) throw new Error('No file provided');

		const dotIndex = file.name.lastIndexOf('.');

        let cleanPath = path.startsWith("public/")
            ? path.substring("public/".length)
            : path;
        // File without extension (or hidden file like ".env") keeps the whole name and gets no ".ext" suffix
        const fileKey = dotIndex <= 0
            ? `${cleanPath}/uploads/${file.name}_${uuidv4()}`
            : `${cleanPath}/uploads/${file.name.substring(0, dotIndex)}_${uuidv4()}.${file.name.substring(dotIndex + 1)}`;

        // Upload file
        await Storage.put(fileKey, file, {
            contentType: file.type,
            level: 'public',
            checksumAlgorithm: useChecksum ? 'crc-32' : undefined
        });
        
		return `public/${fileKey}`;
    } catch (error) {
        console.error('Error uploading file:', error);
        return null;
    }
};

/**
 * Upload files (documents) to S3 for AI Chat
 * @param {string} path - S3 path prefix
 * @param {Object[]} listFiles - List of file objects with id and fileData
 * @returns {Object[]} - List of uploaded file data with s3Path (null if the upload failed)
 */
export const uploadFilesTmpOnAiChat = async (path, listFiles) => {
    if (Utils.isEmptyArray(listFiles)) return [];

    const putFileS3 = await Promise.all(
        listFiles.map(async file => {
            const uploaded = await uploadImageToS3(path, file.fileData, true);
            return {
                id: file?.id,
                s3Path: uploaded,
                fileName: file?.fileName,
                fileExtension: file?.fileExtension,
                fileSize: file?.fileSize
            };
        })
    );

    // Keep failed uploads (s3Path = null) so the caller can mark them as ERROR
    return putFileS3;
};

